import { Injectable, inject } from '@angular/core';
import { Auth, signInWithEmailLink, sendSignInLinkToEmail, signOut, onAuthStateChanged, User as FirebaseUser, GoogleAuthProvider, signInWithPopup, sendPasswordResetEmail, signInWithCustomToken } from '@angular/fire/auth';
import { Firestore, doc, getDoc, collection, addDoc, serverTimestamp, getDocs } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject, Observable, from, filter, map } from 'rxjs';
import { User } from '../models/user';

interface CustomClaims {
  access: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth: Auth = inject(Auth);
  private firestore: Firestore = inject(Firestore);
  private functions: Functions = inject(Functions);
  private googleProvider = new GoogleAuthProvider();

  private userSubject = new BehaviorSubject<User | null>(null);
  user$ = this.userSubject.asObservable();

  constructor() {
    onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (firebaseUser) {
        const user = await this.getUserDataFromFirestore(firebaseUser);
        this.userSubject.next(user);
      } else {
        this.userSubject.next(null);
      }
    });
  }

  private async checkUserExists(email: string): Promise<boolean> {
    try {
      // Call Cloud Function to check if email exists
      const checkEmailExists = httpsCallable(this.functions, 'checkEmailExists');
      const result = await checkEmailExists({ email: email.toLowerCase().trim() });
      return result.data as boolean;
    } catch (error: any) {
      console.error('Error checking user existence:', error);
      // For any errors, we'll assume the account doesn't exist
      return false;
    }
  }

  async sendLoginOTP(email: string): Promise<void> {
    const callable = httpsCallable(this.functions, 'sendLoginOTP');
    try {
      await callable({ email });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to send OTP');
    }
  }

  async verifyLoginOTP(email: string, otp: string): Promise<User> {
    const callable = httpsCallable(this.functions, 'verifyLoginOTP');
    try {
      const result = await callable({ email, otp }) as any;
      const token = result.data.token;

      const userCredential = await signInWithCustomToken(this.auth, token);
      const user = await this.getUserDataFromFirestore(userCredential.user);
      this.userSubject.next(user);
      return user;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to verify OTP');
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut(this.auth);
      this.userSubject.next(null);
    } catch (error: any) {
      throw new Error(error.message || 'Logout failed');
    }
  }

  getUserData(): Observable<User> {
    return this.user$.pipe(
      filter((user): user is User => user !== null)
    );
  }

  private async getUserDataFromFirestore(firebaseUser: FirebaseUser): Promise<User> {
    const userDoc = await getDoc(doc(this.firestore, `users/${firebaseUser.uid}`));
    if (!userDoc.exists()) {
      throw new Error('Your account is not set up. Please contact your administrator.');
    }
    return userDoc.data() as User;
  }

  async isAuthenticated(): Promise<boolean> {
    return !!this.auth.currentUser;
  }

  async isEmailVerified(): Promise<boolean> {
    return this.auth.currentUser?.emailVerified ?? false;
  }

  getCurrentUserId() {
    return this.auth.currentUser?.uid;
  }

  createAccount(data: any) {
    const callable = httpsCallable(this.functions, 'accountCreate');
    return callable(data);
  }

  async signInWithGoogle(): Promise<User> {
    try {
      const result = await signInWithPopup(this.auth, this.googleProvider);
      const user = await this.getUserDataFromFirestore(result.user);
      this.userSubject.next(user);
      return user;
    } catch (error: any) {
      throw new Error(error.message || 'Google sign-in failed');
    }
  }

  logError(
    functionLocation: string,
    whichFunction: string,
    errorMessage: string,
    createdByEmail: string
  ) {
    const errorLogsCollection = collection(this.firestore, 'errorLogs');
    return addDoc(errorLogsCollection, {
      functionLocation,
      whichFunction,
      errorMessage,
      createdAt: serverTimestamp(),
      createdBy: this.currentUser,
      createdByEmail,
    });
  }

  get currentUser() {
    return this.auth.currentUser?.uid;
  }

  getUserClaimsByUid(uid: string): Promise<any> {
    const callable = httpsCallable(this.functions, 'userClaims');
    return callable({ id: uid }).then(result => result.data);
  }

  async checkUserAccess(): Promise<boolean> {
    const idToken = await this.auth.currentUser?.getIdTokenResult();
    if (!idToken?.claims) return false;
    const claims = idToken.claims as unknown as CustomClaims;
    return claims?.access > 0 && claims?.access < 4;
  }

  async getUserAccessClaim(): Promise<number> {
    const idToken = await this.auth.currentUser?.getIdTokenResult();
    if (!idToken?.claims) return 0;
    const claims = idToken.claims as unknown as CustomClaims;
    return claims?.access || 0;
  }

  getEntitiesByOrganisation(organisationId: string): Observable<{ id: string, name: string }[]> {
    const entitiesRef = collection(this.firestore, 'entities');
    return from(getDocs(entitiesRef)).pipe(
      map(snapshot =>
        snapshot.docs
          .filter(doc => doc.data()['organisation']?.id === organisationId)
          .map(doc => ({
            id: doc.id,
            name: doc.data()['name']
          }))
      )
    );
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  }

  async refreshUserData(): Promise<User> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) {
      throw new Error('No user logged in');
    }
    const user = await this.getUserDataFromFirestore(firebaseUser);
    this.userSubject.next(user);
    return user;
  }
}

